/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  LabelValue,
  WbsLabelValueSearchResult,
  WbsLabelValueSearchInput
} from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { mappedLabelValues, LabelValueType } from '../LabelFacetDetails';
import { scoredSuggestions } from '../scoredSuggestions';
import {
  searchProjects,
  searchProjectsByTerm
} from './hooks/useProjectLabelSearch';

export const cleanSearchTerm = (value: string): string =>
  value.replace(/['",]+/g, '');

type SearchProjectsHandler = (
  input: WbsLabelValueSearchInput
) => Promise<WbsLabelValueSearchResult[]>;

const matchOption = (
  searchClients: SearchProjectsHandler,
  labelId: string
) => async (_: LabelValue[], term: string): Promise<LabelValueType | null> => {
  if (!term) return null;

  const clients = await searchClients({ query: cleanSearchTerm(term) });
  const mappedValues = mappedLabelValues(clients, labelId);
  const option = mappedValues && mappedValues.length > 0 && mappedValues[0];
  return option && option.displayText ? option : null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>,
  label: TimesheetLabel
) => (BaseComponent: React.FunctionComponent): React.FC => (props: any) => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState<LabelValueType[]>(
    criterions[tag] || []
  );
  const apolloClient = useApolloClient();
  const $searchProjects = searchProjects({
    client: apolloClient
  });
  const searchProjectsSuggestion = searchProjectsByTerm({
    client: apolloClient
  });
  const value = useMemo(
    () => ({
      selected,
      setSelected,
      options: [],
      matchOption: matchOption($searchProjects, label.id),
      searchProjects: $searchProjects,
      suggestions: scoredSuggestions(searchProjectsSuggestion)
    }),
    [selected, setSelected, $searchProjects, searchProjectsSuggestion]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

type LabelCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (
    options: LabelValue[],
    term: string
  ) => Promise<LabelValueType | null>;
  searchProjects: SearchProjectsHandler;
};

const makeCriteriaProvider = (label: TimesheetLabel) => (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>
): ((BaseComponent: React.FunctionComponent) => React.FC) =>
  withCriteriaProvider(tag, context, label);

export default makeCriteriaProvider;
